"use client";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { AlertCircleIcon, Loader2Icon, XIcon } from "lucide-react";
import { unstable_rethrow } from "next/navigation";
import React, {
  useActionState,
  useCallback,
  useMemo,
  useState,
  useTransition,
} from "react";
import { toast } from "sonner";
import { useServerActionErrorHandler } from "@/app/[lang]/_over-capacity";
import { useTrackEvent } from "@/components/analytics/events";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AutocompleteInput } from "@/components/ui/autocomplete-input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ColorPicker, DEFAULT_COLORS } from "@/components/ui/color-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { isDemoMode } from "@/config";
import { logger } from "@/lib/logger";
import { useRouter } from "@/lib/navigation";
import { useRefreshWorkspaceSidebar } from "../../_contexts/workspace-sidebar-data-context";
import { createProject } from "../../action";
import { BigQuerySelector } from "../../ua/_components/bigquery-selector";
import { CategoryTree } from "../../ua/_components/category-tree";
import { PresetCards } from "../../ua/_components/preset-cards";
import { useBigQuery } from "../../ua/_hooks/use-bigquery";
import { usePresetSelection } from "../../ua/_hooks/use-preset-selection";
import { getAllPresetsAsViewPresets } from "../../ua/_utils/preset-config";

// Common GA4 event names for suggestions
const COMMON_KEY_EVENTS = [
  "purchase",
  "add_to_cart",
  "begin_checkout",
  "view_item",
  "add_to_wishlist",
  "generate_lead",
  "login",
  "sign_up",
  "search",
  "select_content",
  "share",
  "video_start",
  "video_progress",
  "video_complete",
  "download",
  "file_download",
  "form_submit",
  "form_start",
  "page_view",
  "scroll",
  "click",
  "view_promotion",
  "select_promotion",
  "add_payment_info",
  "add_shipping_info",
  "view_cart",
  "remove_from_cart",
  "view_item_list",
  "select_item",
  "tutorial_begin",
  "tutorial_complete",
  "level_up",
  "unlock_achievement",
  "spend_virtual_currency",
  "earn_virtual_currency",
] as const;

interface CreateUAProjectDialogProps {
  workspaceId: string;
  trigger: React.ReactNode;
  userAnalyticsBigQueries?: {
    id: string;
    projectID: string;
    datasetName: string;
    latestDataDate?: string;
  }[];
  workspaceUsedColors?: string[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * User Analytics Project Creation Dialog
 */
export const CreateUAProjectDialog = React.memo(function CreateUAProjectDialog({
  workspaceId,
  trigger,
  workspaceUsedColors,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: CreateUAProjectDialogProps) {
  const trackEvent = useTrackEvent();
  const { i18n } = useLingui();
  const { handleServerActionError } = useServerActionErrorHandler();
  const router = useRouter();
  const refreshWorkspaceSidebar = useRefreshWorkspaceSidebar();
  const encodedWorkspaceId = encodeURIComponent(workspaceId);

  // Dialog state
  const [internalOpen, setInternalOpen] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = useCallback(
    (value: boolean) => {
      if (controlledOnOpenChange) {
        controlledOnOpenChange(value);
      } else {
        setInternalOpen(value);
      }
    },
    [controlledOnOpenChange],
  );

  // Form state
  const [errors, setErrors] = useState<string[]>([]);
  const [selectedColor, setSelectedColor] = useState<string>(
    DEFAULT_COLORS[0] || "#000000",
  );

  // Key events state (新規作成時は常に空配列で開始)
  const [keyEvents, setKeyEvents] = useState<string[]>([]);
  const [tempKeyEvent, setTempKeyEvent] = useState<string>("");

  const addKeyEvent = useCallback((keyEventValue: string) => {
    if (keyEventValue.trim() === "") return;
    setKeyEvents((prev) => {
      if (prev.includes(keyEventValue.trim())) {
        return prev; // 重複時は何もしない
      }
      return [...prev, keyEventValue.trim()];
    });
    setTempKeyEvent("");
  }, []);

  const removeKeyEvent = useCallback((event: string) => {
    setKeyEvents((prev) => prev.filter((e) => e !== event));
  }, []);

  // Get presets
  const viewPresets = useMemo(() => {
    return getAllPresetsAsViewPresets(i18n);
  }, [i18n]);

  // Custom hooks for separated concerns
  const presetSelection = usePresetSelection(viewPresets, i18n, true); // 新規プロジェクト作成時はデフォルトで全選択
  const bigQueryData = useBigQuery(workspaceId, open);

  // Form action with validation
  const handleCreateProject = useCallback(
    async (_prevState: unknown, formData: FormData) => {
      try {
        setErrors([]);

        const name = formData.get("name") as string;
        const color = formData.get("color") as string;
        const bigQueryId = formData.get("bigQueryId") as string;

        // Server action call - This will redirect on success
        const result = await createProject(
          workspaceId,
          name?.trim() || "",
          "ua" as const,
          color,
          bigQueryId,
          presetSelection.selectedPresetIds,
          keyEvents.length > 0 ? keyEvents : undefined,
        );

        if (result.errors && result.errors.length > 0) {
          setErrors(result.errors);
          logger.warn("Project creation validation failed:", result.errors);

          // Over Capacityエラーをチェックしてモーダル表示
          handleServerActionError(result);

          return result;
        }

        // プロジェクトが正常に作成された場合
        if (result.projectId) {
          trackEvent({
            eventName: "ua_project_created",
            payload: { product_area: "ua" },
          });

          // プロジェクトページへ遷移
          // UAでBigQuery設定が指定されていれば一覧画面に、そうでなければ設定ページに遷移
          const projectPath = `/${i18n.locale}/${encodedWorkspaceId}/ua/${encodeURIComponent(result.projectId)}${
            bigQueryId ? "" : "/settings"
          }`;
          router.push(projectPath);
          router.refresh();

          // 成功メッセージを表示
          toast.success(i18n._(msg`User Analytics project created`));

          // ダイアログを閉じる
          setOpen(false);
          refreshWorkspaceSidebar("project-mutation");
        }

        return result;
      } catch (error) {
        unstable_rethrow(error);
        const errorMessage = i18n._(
          msg`An unexpected error occurred. Please try again.`,
        );
        setErrors([errorMessage]);
        logger.error("Unexpected error during project creation:", error);
        return { errors: [errorMessage] };
      }
    },
    [
      workspaceId,
      encodedWorkspaceId,
      presetSelection.selectedPresetIds,
      keyEvents,
      i18n,
      router,
      setOpen,
      handleServerActionError,
      trackEvent,
      refreshWorkspaceSidebar,
    ],
  );

  const [_state, formAction, isPending] = useActionState(
    handleCreateProject,
    null,
  );

  const [isTransitionPending, startTransition] = useTransition();

  // Submit handler that properly uses startTransition
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const form = e.currentTarget as HTMLFormElement;
      const formData = new FormData(form);
      formData.set("color", selectedColor);
      formData.set("bigQueryId", bigQueryData.selectedBigQueryId);

      // Call formAction inside startTransition to avoid the warning
      startTransition(() => {
        formAction(formData);
      });
    },
    [selectedColor, bigQueryData.selectedBigQueryId, formAction],
  );

  const pending = isPending || isTransitionPending;

  return (
    <>
      {/* Trigger button */}
      {React.cloneElement(
        trigger as React.ReactElement<{ onClick?: () => void }>,
        {
          onClick: () => setOpen(true),
        },
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle>
              <Trans>Create User Analytics Project</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans>
                Set up a new User Analytics project to track and analyze user
                behavior.
              </Trans>
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={handleSubmit}
            className="flex-1 overflow-hidden flex flex-col min-h-0"
          >
            <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
              <div className="space-y-4">
                {/* Error Display */}
                {errors.length > 0 && (
                  <Alert variant="destructive">
                    <AlertCircleIcon className="h-4 w-4" />
                    <AlertTitle>
                      <Trans>Error</Trans>
                    </AlertTitle>
                    <AlertDescription>
                      {errors.map((error, index) => (
                        <div key={index}>{error}</div>
                      ))}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Project Name */}
                <div className="flex flex-col gap-2">
                  <Label htmlFor="name">
                    <Trans>Project Name</Trans>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder={i18n._(msg`Enter project name`)}
                    required
                    maxLength={100}
                    disabled={pending}
                    autoFocus
                    autoComplete="off"
                    data-1p-ignore
                    data-lpignore="true"
                  />
                </div>

                {/* Color Selection */}
                <div className="flex flex-col gap-2">
                  <Label>
                    <Trans>Project Color</Trans>
                  </Label>
                  <ColorPicker
                    name="color"
                    value={selectedColor}
                    onChange={setSelectedColor}
                    colors={DEFAULT_COLORS}
                    usedColors={workspaceUsedColors}
                    required
                    disabled={pending}
                  />
                </div>

                {/* BigQuery Configuration */}
                <BigQuerySelector
                  bigQueries={bigQueryData.bigQueries}
                  selectedBigQueryId={bigQueryData.selectedBigQueryId}
                  isFetching={bigQueryData.isFetching}
                  onBigQueryChange={bigQueryData.handleBigQueryChange}
                  i18n={i18n}
                  workspaceId={workspaceId}
                  onNavigateToSettings={() => setOpen(false)}
                />

                {/* Key Event Settings (Optional) */}
                <div className="space-y-2">
                  <AutocompleteInput
                    value={tempKeyEvent}
                    onChange={setTempKeyEvent}
                    onAdd={addKeyEvent}
                    suggestions={[...COMMON_KEY_EVENTS]}
                    existingItems={keyEvents}
                    placeholder={i18n._(
                      msg`Type or select a key event (e.g., purchase)`,
                    )}
                    label={<Trans>Key Events (Optional)</Trans>}
                    addButtonLabel={<Trans>Add</Trans>}
                  />
                  <div className="flex flex-wrap gap-2 mt-2 min-h-[2rem] items-start">
                    {keyEvents.length > 0 ? (
                      keyEvents.map((event) => (
                        <Badge
                          key={event}
                          variant="outline"
                          className="flex items-center h-8"
                        >
                          {event}
                          <button
                            type="button"
                            onClick={() => removeKeyEvent(event)}
                            className="ml-2 hover:bg-destructive/20 rounded-sm"
                            disabled={pending}
                          >
                            <XIcon className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))
                    ) : (
                      <p className="text-xs text-muted-foreground flex items-center h-8">
                        <Trans>
                          Define important events to track conversions. Leave
                          empty to skip.
                        </Trans>
                      </p>
                    )}
                  </div>
                </div>

                {/* Preset Selection */}
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <Label>
                      <Trans>Create Views from Presets</Trans>
                    </Label>
                    <span className="px-2 py-0.5 text-xs font-medium bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-full shadow-sm">
                      Beta
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    <Trans>
                      Select presets based on your analysis needs (optional)
                    </Trans>
                  </p>

                  <ResizablePanelGroup
                    direction="horizontal"
                    className="min-h-[400px] h-[400px] rounded-lg border"
                  >
                    {/* Category Tree */}
                    <ResizablePanel defaultSize={25} minSize={20} maxSize={40}>
                      <CategoryTree
                        categories={presetSelection.categories}
                        selectedPresetIds={presetSelection.selectedPresetIds}
                        expandedCategories={presetSelection.expandedCategories}
                        onPresetToggle={presetSelection.handlePresetToggle}
                        onCategoryToggle={presetSelection.handleCategoryToggle}
                        onSelectAll={presetSelection.handleSelectAll}
                        viewPresets={viewPresets}
                      />
                    </ResizablePanel>

                    <ResizableHandle withHandle />

                    {/* Preset Cards - 2 column layout */}
                    <ResizablePanel defaultSize={75}>
                      <PresetCards
                        presets={presetSelection.filteredPresets}
                        selectedPresetIds={presetSelection.selectedPresetIds}
                        searchQuery={presetSelection.searchQuery}
                        onSearchChange={presetSelection.setSearchQuery}
                        onPresetToggle={presetSelection.handlePresetToggle}
                      />
                    </ResizablePanel>
                  </ResizablePanelGroup>

                  {/* Selected presets display (always visible) */}
                  <div className="mt-3 p-3 bg-muted/30 rounded-lg min-h-[120px]">
                    <div className="text-xs font-semibold text-foreground mb-2">
                      <Trans>Selected Presets</Trans>{" "}
                      {presetSelection.selectedPresetIds.length > 0 &&
                        `(${presetSelection.selectedPresetIds.length})`}
                    </div>
                    <div className="flex flex-wrap gap-2 min-h-[80px] content-start">
                      {presetSelection.selectedPresetIds.length > 0 ? (
                        presetSelection.selectedPresetIds.map((presetId) => {
                          const preset = viewPresets.find(
                            (p) => p.presetID === presetId,
                          );
                          if (!preset) return null;
                          return (
                            <div
                              key={presetId}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-xs h-fit"
                            >
                              <span className="truncate max-w-[200px]">
                                {preset.name}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  presetSelection.handlePresetToggle(presetId)
                                }
                                className="ml-1 hover:bg-primary/20 rounded p-0.5"
                              >
                                <XIcon className="h-3 w-3" />
                              </button>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-xs text-muted-foreground flex items-center">
                          <Trans>No presets selected</Trans>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="px-6 py-4 border-t">
              <Button
                type="submit"
                disabled={
                  pending ||
                  isDemoMode ||
                  (bigQueryData.hasFetched &&
                    bigQueryData.bigQueries.length === 0) ||
                  (bigQueryData.hasFetched &&
                    bigQueryData.bigQueries.length > 0 &&
                    !bigQueryData.selectedBigQueryId)
                }
                className="w-20"
              >
                {pending ? (
                  <Loader2Icon className="h-4 w-4 animate-spin" />
                ) : (
                  <Trans>Create</Trans>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
});
